import { useCallback, useState } from "react";

import { useLexicalComposerContext } from "@lexical/react/LexicalComposerContext";
import { clsx } from "clsx";
import { $getSelection, $isRangeSelection, TextNode } from "lexical";
import uniqBy from "lodash/uniqBy";
import * as ReactDOM from "react-dom";

import { $createCustomVariableNode, $isCustomVariableNode } from "../../nodes/CustomVariableNode";
import { LexicalTriggerMenuPlugin } from "../../vendor/LexicalTriggerMenuPlugin";
import { ConfigurableVariableMenuItem } from "./Components/ConfigurableVariableMenuItem/ConfigurableVariableMenuItem";
import { ConfigurableVariableOption } from "./ConfigurableVariableOption";
import { useTypeaheadTriggerMatch } from "./Hooks/useTypeaheadTriggerMatch/useTypeaheadTriggerMatch";

import type { LexicalTrigger } from "../../types";

import styles from "./ConfigurableVariablesPickerPlugin.module.scss";

type CustomVariablePickerPluginProps = {
    popAbove?: boolean;
    fixedWidth?: boolean;
    prioritizedTriggers: Array<LexicalTrigger>;
    forceWrap?: LexicalTrigger;
    loading: boolean;
    options: Array<ConfigurableVariableOption>; //MenuOption ?
    withinModal?: boolean;
    /** Shifts the rendered menu up by this many pixels. Useful when a clipped/overflowed
     *  container would otherwise cut off the popover at the bottom of the page. */
    verticalOffset?: number;
    /** Fires when the typeahead menu opens (a trigger match becomes active). */
    onOpen?: () => void;
    /** Fires when the typeahead menu closes. */
    onClose?: () => void;
};

export function ConfigurableVariablesPickerPlugin({
    popAbove = false,
    fixedWidth = false,
    withinModal = false,
    loading,
    options,
    prioritizedTriggers,
    forceWrap,
    verticalOffset,
    onOpen,
    onClose,
}: CustomVariablePickerPluginProps) {
    const [editor] = useLexicalComposerContext();
    const [typeaheadOptions, setTypeaheadOptions] = useState(options);
    const [mouseX, setMouseX] = useState(-1);
    const [mouseY, setMouseY] = useState(-1);

    const menuOffsetStyle = verticalOffset ? { transform: `translateY(-${verticalOffset}px)` } : undefined;

    const { triggerCallbacks } = useTypeaheadTriggerMatch({
        prioritizedTriggers,
    });

    const handleQueryChange = useCallback(
        (query: string | null) => {
            let filteredOptions = options;
            const safeQuery = query?.trim()?.toLowerCase();
            if (safeQuery) {
                filteredOptions = filteredOptions.filter((option) => option.value.toLowerCase().includes(safeQuery));
            }
            setTypeaheadOptions(uniqBy(filteredOptions, "value"));
        },
        [options]
    );

    const onSelectOption = useCallback(
        (
            selectedOption: ConfigurableVariableOption,
            nodeToRemove: TextNode | null,
            closeMenu: () => void,
            matchedString: string,
            openTag?: string,
            closeTag?: string
        ) => {
            editor.update(() => {
                if (forceWrap) {
                    openTag = forceWrap.openingTag;
                    closeTag = forceWrap.closingTag;
                }
                // default to double curly braces
                openTag = openTag || "{{";
                closeTag = closeTag || "}}";
                const selection = $getSelection();

                if (!$isRangeSelection(selection) || selectedOption == null) {
                    return;
                }

                if (nodeToRemove) {
                    // this represents the text node that triggered the custom variable picker menu
                    // we want to remove that partial text (used as a query to search for custom variables)
                    // and replace it with the selected custom variable node
                    nodeToRemove.remove();
                } else {
                    const [currentNode] = selection.getNodes();
                    if ($isCustomVariableNode(currentNode) && matchedString.length) {
                        // we want to remove the node under the cursor
                        // since we're going to insert the selected CustomVariableNode
                        // at the same cursor position
                        currentNode.remove();
                    }
                }

                selection.insertNodes([$createCustomVariableNode(`${openTag}${selectedOption.text}${closeTag}`)]);

                closeMenu();
            });
        },
        [editor, forceWrap]
    );

    return (
        <LexicalTriggerMenuPlugin
            onQueryChange={handleQueryChange}
            onSelectOption={onSelectOption}
            onOpen={onOpen ? () => onOpen() : undefined}
            onClose={onClose}
            triggerFns={triggerCallbacks}
            options={typeaheadOptions}
            fixedMenu={popAbove} // if forced pop above, fix menu to not reposition
            menuRenderFn={(
                anchorElementRef,
                { selectedIndex, options, selectOptionAndCleanUp, setHighlightedIndex }
            ) => {
                if (anchorElementRef.current == null) {
                    return null;
                }

                if (loading) {
                    return ReactDOM.createPortal(
                        <div
                            className={clsx("typeahead-popover custom-variables-menu", {
                                "typeahead-popover-popabove": popAbove,
                            })}
                            style={menuOffsetStyle}
                        >
                            <div className={styles.loading}>Loading...</div>
                        </div>,
                        anchorElementRef.current
                    );
                }

                return anchorElementRef.current && options.length
                    ? ReactDOM.createPortal(
                          <div
                              className={clsx(styles.menuContainer, "typeahead-popover custom-variables-menu", {
                                  "typeahead-popover-popabove": popAbove,
                                  "typeahead-popover-fixedsize": fixedWidth,
                                  [styles.withinModal]: withinModal,
                              })}
                              style={menuOffsetStyle}
                          >
                              <ul>
                                  {options.map((option: ConfigurableVariableOption, index) => (
                                      <ConfigurableVariableMenuItem
                                          key={`${index} ${option.value}`}
                                          isSelected={selectedIndex === index}
                                          onClick={() => {
                                              setHighlightedIndex(index);
                                              selectOptionAndCleanUp(option);
                                          }}
                                          onMouseEnter={(event) => {
                                              if (event.pageX !== mouseX || event.pageY !== mouseY) {
                                                  setMouseX(event.pageX);
                                                  setMouseY(event.pageY);
                                                  setHighlightedIndex(index);
                                              }
                                          }}
                                          onMouseMove={() => {
                                              if (index !== selectedIndex) {
                                                  setHighlightedIndex(index);
                                              }
                                          }}
                                          option={option}
                                      />
                                  ))}
                              </ul>
                          </div>,
                          anchorElementRef.current
                      )
                    : null;
            }}
        />
    );
}
